import { db, auth, functions } from '@/js/firebase';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  // Timestamp,
  onSnapshot,
  orderBy,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { logger } from './loggerService';
import { toastBridge } from './toastBridge';
import { deleteDeliverableSubcollections } from './firestoreUtils';
import { DELIVERABLE_STAGES } from '@/utils/deliverableUtils';
import { getEpisodeSlotCompleteness } from '@/utils/reviewUtils';
import { valuesEqual } from '@/utils/valueCompare';

class DeliverableService {
  constructor() {
    this.logger = logger;
    this.toast = toastBridge;
  }

  // Create a new deliverable
  async createDeliverable(deliverableData, { showToast = true } = {}) {
    try {
      const deliverablesRef = collection(db, 'deliverables');

      const newDeliverable = {
        ...deliverableData,
        productionEventIds: deliverableData.productionEventIds || [],
        dependencies: deliverableData.dependencies || [],
        linkedDeliverableIds: deliverableData.linkedDeliverableIds || [],
        isPrimaryEpisodeDeliverable: deliverableData.isPrimaryEpisodeDeliverable || false,
        // Marks the new slot-based episode review flow so automation rules can tell it
        // from legacy free-form episodes (whose docs simply lack the field).
        // ponytail: stamped at creation, never on update — a primary-episode deliverable
        // created from here on always gets the slot UI. Do NOT mirror this from
        // isPrimaryEpisodeDeliverable on update: DeliverableModal spreads its whole form
        // into updateDeliverable, so an unrelated title edit would restamp a legacy
        // episode as slot-based. Ceiling: ticking the primary checkbox AFTER creation
        // leaves this false — fix such a deliverable with an update_field rule.
        usesEpisodeSlots: deliverableData.isPrimaryEpisodeDeliverable || false,
        stage: deliverableData.stage || 'not_ready', // Default stage
        // Same reason as stage: a missing or empty status matches no filter key,
        // which makes the deliverable invisible on every board and table.
        status: deliverableData.status || 'not_started',
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(deliverablesRef, newDeliverable);

      // Log the creation activity
      await this.logActivity(
        docRef.id,
        'created',
        null,
        null,
        deliverableData.title,
        `Created deliverable: ${deliverableData.title}`
      );

      // Log initial assignment if a user was assigned
      if (deliverableData.assignedUserId) {
        await this.logActivity(
          docRef.id,
          'assigned',
          'assignedUserId',
          null,
          deliverableData.assignedUserId,
          'Initial assignment'
        );
      }

      this.logger.info('DeliverableService', `Deliverable created with ID: ${docRef.id}`);
      if (showToast) this.toast.success('Deliverable created successfully');

      return { success: true, id: docRef.id };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to create deliverable: ${error.message}`);
      if (showToast) this.toast.error('Failed to create deliverable');
      throw error;
    }
  }

  /**
   * Create multiple deliverables in sequence without per-item toasts.
   * The caller owns user messaging. Failures are collected, not thrown, so a
   * single bad doc does not abort the whole batch.
   * @param {Array<Object>} deliverableArray - deliverable docs to create
   * @returns {Promise<{ created: string[], failed: Array<{ data: Object, error: string }> }>}
   */
  async createDeliverablesBatch(deliverableArray = []) {
    const created = [];
    const failed = [];

    for (const data of deliverableArray) {
      try {
        const result = await this.createDeliverable(data, { showToast: false });
        created.push(result.id);
      } catch (error) {
        failed.push({ data, error: error.message });
        this.logger.error('DeliverableService', `Batch create failed for "${data.title}": ${error.message}`);
      }
    }

    this.logger.info('DeliverableService', `Batch created ${created.length}/${deliverableArray.length} deliverables`);
    return { created, failed };
  }

  // Update an existing deliverable
  // Read a primary-episode deliverable's review items + episode project and
  // compute slot readiness (all slots filled + the 5 videos internally approved).
  // reviewId === deliverableId. Fails open (isComplete:true) only if the review
  // truly has no items would be wrong — so a read error blocks (isComplete:false).
  async _checkEpisodeSlotsReady(deliverableId, projectId, clientProvidesThumbnail = false, anthemDistributes = true) {
    try {
      const [itemsSnap, projSnap] = await Promise.all([
        getDocs(collection(db, 'reviews', deliverableId, 'items')),
        projectId ? getDoc(doc(db, 'projects', projectId)) : Promise.resolve(null),
      ]);
      const items = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Legacy skip: a review with items but none slotted predates the slot
      // feature — don't gate it (only new slot-based episodes are enforced).
      if (items.length > 0 && !items.some((it) => it.slot)) {
        return { isComplete: true, missing: [] };
      }
      const project = projSnap?.exists() ? projSnap.data() : {};
      return getEpisodeSlotCompleteness(items, project, { requireApproved: true, clientProvidesThumbnail, anthemDistributes });
    } catch (error) {
      this.logger.error('DeliverableService', `Episode slot readiness check failed for ${deliverableId}`, { error: error.message });
      return { isComplete: false, missing: ['(could not verify slots)'] };
    }
  }

  async updateDeliverable(deliverableId, updates, showToast = true, options = {}) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);

      // Get current deliverable for comparison
      const currentDoc = await getDoc(deliverableRef);
      const currentData = currentDoc.data();

      // Episode client-review gate: a primary-episode deliverable can't advance
      // to client review until every asset slot is filled AND internally
      // approved (title/thumbnail/notes set too). This is the single frontend
      // chokepoint — skipInternalReview, the stage dropdown, and kanban all
      // funnel through here. Automation is gated separately in ruleEngine.
      if (
        updates.stage === DELIVERABLE_STAGES.CLIENT_REVIEW &&
        currentData?.stage !== DELIVERABLE_STAGES.CLIENT_REVIEW &&
        currentData?.isPrimaryEpisodeDeliverable
      ) {
        const { isComplete, missing } = await this._checkEpisodeSlotsReady(
          deliverableId,
          currentData.projectId,
          currentData.clientProvidesThumbnail,
          currentData.anthemDistributes !== false
        );
        if (!isComplete) {
          const msg = `Fill and approve all episode slots before client review (${missing.length} remaining).`;
          if (showToast) this.toast.error(msg);
          throw Object.assign(new Error(msg), { isValidation: true });
        }
      }

      // Validation: Prevent status changes if stage is "not_ready"
      // Allow if the same update is also moving stage away from not_ready
      if (updates.status && updates.status !== currentData.status) {
        const effectiveStage = updates.stage || currentData.stage || 'not_ready';
        if (effectiveStage === 'not_ready') {
          this.logger.warn('DeliverableService', `Cannot change status for deliverable ${deliverableId} - stage is "not_ready"`);
          this.toast.error('Cannot change status when deliverable stage is "Not Ready"');
          throw Object.assign(new Error('Cannot change status when stage is "not_ready"'), { isValidation: true });
        }
      }

      const cleanedUpdates = { ...updates };
      let isClientReviewReset = false;

      // Manual move back into client review: clear the stale client-review
      // aggregate. Left in place, a repeat "changes requested" writes the same
      // value over itself and `changed_to` rules never fire (ruleEngine requires
      // oldValue !== newValue) — the deliverable then sits in client_review.
      // Only when the caller isn't already setting these (automation/presets do).
      if (
        updates.stage === DELIVERABLE_STAGES.CLIENT_REVIEW &&
        currentData?.stage !== DELIVERABLE_STAGES.CLIENT_REVIEW &&
        !('clientReviewStatus' in updates)
      ) {
        Object.assign(cleanedUpdates, {
          clientReviewStatus: null,
          clientReviewStatusAt: null,
          clientReviewStatusBy: null,
          clientReviewComplete: false,
          clientReviewDecided: false,
          clientReviewDecidedAt: null,
          clientReviewDecidedBy: null
        });
        isClientReviewReset = currentData?.clientReviewStatus != null ||
          !!currentData?.clientReviewComplete || !!currentData?.clientReviewDecided;
      }

      // Remove fields that shouldn't be updated directly
      delete cleanedUpdates.createdAt;
      delete cleanedUpdates.createdBy;
      delete cleanedUpdates.createdByName;
      delete cleanedUpdates.updatedAt;
      delete cleanedUpdates.updatedBy;
      delete cleanedUpdates.updatedByName;

      // Add updatedBy for notification triggers to identify who made the change
      cleanedUpdates.updatedBy = auth.currentUser?.uid || null;
      cleanedUpdates.updatedAt = serverTimestamp();

      await updateDoc(deliverableRef, cleanedUpdates);
      
      // Log significant changes
      for (const [field, newValue] of Object.entries(updates)) {
        const oldValue = currentData[field];
        
        // Skip logging for certain fields
        if (field === 'updatedAt' || field === 'updatedBy' || field === 'updatedByName') {
          continue;
        }
        
        // Only log if the value actually changed. valuesEqual handles the cases
        // a bare !== gets wrong — null/undefined/'' are all "None", and a
        // Timestamp vs a Date for the same instant is not a change.
        if (valuesEqual(oldValue, newValue)) {
          continue;
        }
        
        // Special handling for status changes
        if (field === 'status') {
          await this.logActivity(
            deliverableId,
            'status_change',
            'status',
            oldValue,
            newValue,
            `Status changed from ${oldValue} to ${newValue}`
          );
        } else if (field === 'stage') {
          // Special handling for stage changes
          await this.logActivity(
            deliverableId,
            'stage_change',
            'stage',
            oldValue,
            newValue,
            `Stage changed from ${oldValue || 'Not Set'} to ${newValue}`
          );
        } else {
          await this.logActivity(
            deliverableId,
            'field_update',
            field,
            oldValue,
            newValue,
            `Updated ${field}`
          );
        }
      }

      if (isClientReviewReset) {
        this.logActivity(
          deliverableId,
          'review_state_reset',
          'clientReviewStatus',
          currentData.clientReviewStatus ?? null,
          null,
          'Client review state reset on entering client review'
        );
      }

      this.logger.info('DeliverableService', `Deliverable ${deliverableId} updated`);

      if (showToast) {
        this.toast.success('Deliverable updated successfully');
      }

      // Trigger automation rules evaluation (non-blocking)
      // Skip if this update was from automation to prevent loops
      if (!updates._fromAutomation) {
        this.evaluateAutomationRules(deliverableId, currentData, updates, options.skipRuleIds)
          .catch(err => this.logger.warn('DeliverableService', `Automation evaluation failed: ${err.message}`));
      }

      return { success: true };
    } catch (error) {
      // Validation rejections already toasted their own message — don't double-toast or log as errors
      if (error.isValidation) {
        this.logger.warn('DeliverableService', `Update rejected: ${error.message}`);
        throw error;
      }
      this.logger.error('DeliverableService', `Failed to update deliverable: ${error.message}`);
      this.toast.error('Failed to update deliverable');
      throw error;
    }
  }

  /**
   * Evaluate automation rules for a deliverable update
   * Called after successful update, non-blocking
   * @param {string} deliverableId - The deliverable ID
   * @param {Object} oldData - Data before update
   * @param {Object} newData - Data after update
   * @param {string[]} [skipRuleIds] - Optional list of rule document IDs to exclude
   */
  async evaluateAutomationRules(deliverableId, oldData, newData, skipRuleIds = []) {
    try {
      const callableName = skipRuleIds.length > 0 ? 'evaluateDeliverableRulesFiltered' : 'evaluateDeliverableRules';
      const evaluateRules = httpsCallable(functions, callableName);
      const result = await evaluateRules({
        deliverableId,
        oldData,
        newData,
        triggerType: 'field_change',
        ...(skipRuleIds.length > 0 ? { skipRuleIds } : {})
      });

      if (result.data?.rulesExecuted > 0) {
        this.logger.info('DeliverableService', `Automation rules executed`, {
          deliverableId,
          rulesExecuted: result.data.rulesExecuted
        });
      }
    } catch (error) {
      // Log but don't throw - automation shouldn't block the update
      this.logger.warn('DeliverableService', `Failed to evaluate automation rules: ${error.message}`);
    }
  }

  /**
   * Evaluate calendar_event automation rules (event_linked / event_unlinked)
   * after a production event link change.
   *
   * @param {string} deliverableId - The deliverable ID
   * @param {string} eventAction - 'event_linked' or 'event_unlinked'
   * @param {string} eventId - The calendar event ID
   * @param {string} eventTitle - The calendar event title
   */
  async evaluateCalendarEventRules(deliverableId, eventAction, eventId, eventTitle) {
    try {
      const evaluateRules = httpsCallable(functions, 'evaluateCalendarEventRules');
      const result = await evaluateRules({ deliverableId, eventAction, eventId, eventTitle });

      if (result.data?.rulesExecuted > 0) {
        this.logger.info('DeliverableService', `Calendar event rules executed`, {
          deliverableId,
          eventAction,
          rulesExecuted: result.data.rulesExecuted
        });
      }
    } catch (error) {
      // Log but don't throw - automation shouldn't block the link/unlink
      this.logger.warn('DeliverableService', `Failed to evaluate calendar event rules: ${error.message}`);
    }
  }

  // Delete a deliverable and all its subcollections
  // showToast:false for bulk callers — one summary beats N stacked toasts.
  // Same option-bag shape as createDeliverable.
  async deleteDeliverable(deliverableId, { showToast = true } = {}) {
    try {
      // Get deliverable info before deletion for logging
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableDoc = await getDoc(deliverableRef);
      const deliverableData = deliverableDoc.data();

      // Delete subcollections first (discussions, activityLogs)
      const subcollectionResults = await deleteDeliverableSubcollections(deliverableId);
      this.logger.info('DeliverableService', `Deleted subcollections for deliverable ${deliverableId}`, subcollectionResults);

      // Delete associated review data (storage files, subcollections, share links, review doc)
      // Lazy import to avoid circular dependency (reviewService imports deliverableService)
      const { reviewService } = await import('./reviewService');
      const reviewResult = await reviewService.deleteReview(deliverableId);
      this.logger.info('DeliverableService', `Review cleanup for deliverable ${deliverableId}`, reviewResult);

      // Delete the deliverable document
      await deleteDoc(deliverableRef);

      this.logger.info('DeliverableService', `Deliverable ${deliverableId} deleted (${deliverableData?.title})`);
      if (showToast) this.toast.success('Deliverable deleted successfully');

      return { success: true };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to delete deliverable: ${error.message}`);
      if (showToast) this.toast.error('Failed to delete deliverable');
      throw error;
    }
  }

  // Get a single deliverable
  async getDeliverable(deliverableId) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);
      const deliverableSnap = await getDoc(deliverableRef);
      
      if (deliverableSnap.exists()) {
        return { id: deliverableSnap.id, ...deliverableSnap.data() };
      }
      
      throw new Error(`Deliverable ${deliverableId} not found`);
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to get deliverable: ${error.message}`);
      throw error;
    }
  }

  // Get all deliverables for a project
  async getProjectDeliverables(projectId) {
    try {
      this.logger.info('DeliverableService', `Fetching deliverables for project: ${projectId}`);

      const deliverablesRef = collection(db, 'deliverables');

      // First try simple query without orderBy to avoid index requirement
      const q = query(deliverablesRef, where('projectId', '==', projectId));

      const querySnapshot = await getDocs(q);
      const deliverables = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        this.logger.info('DeliverableService', `Found deliverable: ${doc.id}`, {
          title: data.title,
          hasCompanyId: !!data.companyId,
          companyId: data.companyId
        });
        deliverables.push({ id: doc.id, ...data });
      });

      // Sort manually in JavaScript to avoid index requirement
      if (deliverables.length > 0) {
        deliverables.sort((a, b) => {
          const dateA = a.dueDate?.toMillis ? a.dueDate.toMillis() : (a.dueDate ? new Date(a.dueDate).getTime() : 0);
          const dateB = b.dueDate?.toMillis ? b.dueDate.toMillis() : (b.dueDate ? new Date(b.dueDate).getTime() : 0);
          return dateA - dateB;
        });
      }

      this.logger.info('DeliverableService', `Successfully fetched ${deliverables.length} deliverables for project ${projectId}`);

      return deliverables;
    } catch (error) {
      // Check for permission errors - log these as they indicate a permission issue
      if (error.code === 'permission-denied') {
        this.logger.error('DeliverableService', `Permission denied accessing deliverables for project ${projectId}`);
        this.logger.error('DeliverableService', 'This means the user does not have access to read deliverables for this project');
        return [];
      }

      // Check for index errors (shouldn't happen now but keep for safety)
      if (error.code === 'failed-precondition') {
        this.logger.warn('DeliverableService', 'Index issue detected:', error.message);
        if (error.message && error.message.includes('https://console.firebase.google.com')) {
          const indexUrlMatch = error.message.match(/(https:\/\/console\.firebase\.google\.com[^\s]+)/);
          if (indexUrlMatch) {
            this.logger.info('DeliverableService', `Create index at: ${indexUrlMatch[1]}`);
          }
        }
        // Return empty array for index issues
        return [];
      }

      this.logger.error('DeliverableService', `Failed to get project deliverables: ${error.message}`);
      this.logger.error('DeliverableService', 'Error code:', error.code);
      this.logger.error('DeliverableService', 'Full error:', error);

      // Return empty array instead of throwing to prevent UI crashes
      return [];
    }
  }

  // Get deliverables by production event
  async getDeliverablesByEvent(eventId) {
    try {
      const deliverablesRef = collection(db, 'deliverables');
      const q = query(
        deliverablesRef,
        where('productionEventIds', 'array-contains', eventId)
      );
      
      const querySnapshot = await getDocs(q);
      const deliverables = [];
      
      querySnapshot.forEach((doc) => {
        deliverables.push({ id: doc.id, ...doc.data() });
      });
      
      return deliverables;
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to get deliverables by event: ${error.message}`);
      throw error;
    }
  }

  // Update deliverable stage
  async updateDeliverableStage(deliverableId, newStage) {
    // Delegate to updateDeliverable so all stage changes trigger automations
    // and dependency checks consistently through a single code path
    return this.updateDeliverable(deliverableId, { stage: newStage });
  }

  // Link a production event to a deliverable
  async linkProductionEvent(deliverableId, eventId, eventTitle) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);

      await updateDoc(deliverableRef, {
        productionEventIds: arrayUnion(eventId)
      });
      
      // Log the linking
      await this.logActivity(
        deliverableId,
        'event_linked',
        'productionEventIds',
        null,
        eventId,
        `Linked to production event: ${eventTitle}`
      );
      
      this.logger.info('DeliverableService', `Linked event ${eventId} to deliverable ${deliverableId}`);

      await this.evaluateCalendarEventRules(deliverableId, 'event_linked', eventId, eventTitle);

      return { success: true };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to link production event: ${error.message}`);
      throw error;
    }
  }

  // Unlink a production event from a deliverable
  async unlinkProductionEvent(deliverableId, eventId, eventTitle) {
    try {
      const deliverableRef = doc(db, 'deliverables', deliverableId);

      await updateDoc(deliverableRef, {
        productionEventIds: arrayRemove(eventId)
      });
      
      // Log the unlinking
      await this.logActivity(
        deliverableId,
        'event_unlinked',
        'productionEventIds',
        eventId,
        null,
        `Unlinked from production event: ${eventTitle}`
      );
      
      this.logger.info('DeliverableService', `Unlinked event ${eventId} from deliverable ${deliverableId}`);

      await this.evaluateCalendarEventRules(deliverableId, 'event_unlinked', eventId, eventTitle);

      return { success: true };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to unlink production event: ${error.message}`);
      throw error;
    }
  }

  // Subscribe to real-time deliverable updates for a project
  subscribeToProjectDeliverables(projectId, callback, companyId = null) {
    const deliverablesRef = collection(db, 'deliverables');

    // Build query - include companyId filter if provided (required for client users)
    let q;
    if (companyId) {
      q = query(
        deliverablesRef,
        where('projectId', '==', projectId),
        where('companyId', '==', companyId)
      );
    } else {
      q = query(deliverablesRef, where('projectId', '==', projectId));
    }

    return onSnapshot(q, (snapshot) => {
      const deliverables = [];

      snapshot.forEach((doc) => {
        deliverables.push({ id: doc.id, ...doc.data() });
      });

      // Sort by due date
      deliverables.sort((a, b) => {
        const dateA = a.dueDate?.toMillis ? a.dueDate.toMillis() : 0;
        const dateB = b.dueDate?.toMillis ? b.dueDate.toMillis() : 0;
        return dateA - dateB;
      });

      callback(deliverables);
    }, (error) => {
      // Handle permission errors silently - this is expected for client users
      if (error.code === 'permission-denied') {
        // Return empty array to callback so UI can complete loading
        callback([]);
        return;
      }

      this.logger.error('DeliverableService', `Real-time subscription error: ${error.message}`);
      // Still call callback with empty array to prevent loading state from hanging
      callback([]);
    });
  }

  // Log activity for a deliverable
  async logActivity(deliverableId, action, field, oldValue, newValue, note = null) {
    try {
      const user = auth.currentUser;
      const logsRef = collection(db, 'deliverables', deliverableId, 'activityLogs');

      // Ensure values are never undefined (Firebase doesn't accept undefined)
      const logEntry = {
        deliverableId,  // Store the deliverable ID for easier reporting
        action,
        field: field || null,
        oldValue: oldValue !== undefined ? oldValue : null,
        newValue: newValue !== undefined ? newValue : null,
        userId: user?.uid || 'system',
        userName: user?.displayName || user?.email || 'System',
        timestamp: serverTimestamp(),
        note: note || null
      };

      await addDoc(logsRef, logEntry);

      this.logger.info('DeliverableService', `Activity logged for deliverable ${deliverableId}: ${action}`);
    } catch (error) {
      // Don't throw error for logging failures - we don't want to break the main operation.
      // already-exists means the SDK retried a write that had already committed —
      // the log entry is there, so it's noise rather than a failure.
      const level = error?.code === 'already-exists' ? 'warn' : 'error';
      this.logger[level]('DeliverableService', `Failed to log activity: ${error.message}`);
    }
  }

  // Get activity logs for a deliverable
  // The query reads the whole subcollection regardless, so the cap only hid
  // history — a busy deliverable logs ~5 entries per automation hop.
  async getDeliverableActivity(deliverableId, limit = 1000) {
    try {
      const logsRef = collection(db, 'deliverables', deliverableId, 'activityLogs');
      const q = query(logsRef, orderBy('timestamp', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const logs = [];
      
      querySnapshot.forEach((doc) => {
        if (logs.length < limit) {
          logs.push({ id: doc.id, ...doc.data() });
        }
      });
      
      return logs;
    } catch (error) {
      // If the query fails due to missing index, try without ordering
      if (error.code === 'failed-precondition') {
        // Log the full error message which should contain the index creation URL
        if (error.message && error.message.includes('https://console.firebase.google.com')) {
          this.logger.warn('DeliverableService', `Firestore Index Required for Activity Logs: ${error.message}`);
        }
        
        const logsRef = collection(db, 'deliverables', deliverableId, 'activityLogs');
        const querySnapshot = await getDocs(logsRef);
        const logs = [];
        
        querySnapshot.forEach((doc) => {
          logs.push({ id: doc.id, ...doc.data() });
        });
        
        // Sort manually
        logs.sort((a, b) => {
          const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
          const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
          return timeB - timeA;
        });
        
        return logs.slice(0, limit);
      }
      
      this.logger.error('DeliverableService', `Failed to get activity logs: ${error.message}`);
      return [];
    }
  }

  // Subscribe to activity logs for a deliverable
  subscribeToDeliverableActivity(deliverableId, callback) {
    const logsRef = collection(db, 'deliverables', deliverableId, 'activityLogs');

    return onSnapshot(logsRef, (snapshot) => {
      const logs = [];

      snapshot.forEach((doc) => {
        logs.push({ id: doc.id, ...doc.data() });
      });

      // Sort by timestamp descending
      logs.sort((a, b) => {
        const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : 0;
        const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : 0;
        return timeB - timeA;
      });

      callback(logs);
    }, (error) => {
      this.logger.error('DeliverableService', `Activity subscription error: ${error.message}`);
    });
  }

  // Clone and detach a deliverable (creates standalone deliverable without projectId)
  async cloneAndDetachDeliverable(deliverableId) {
    try {
      // Get the original deliverable
      const originalDeliverable = await this.getDeliverable(deliverableId);

      if (!originalDeliverable) {
        throw new Error(`Deliverable ${deliverableId} not found`);
      }

      // Prevent cloning already-cloned deliverables
      if (originalDeliverable.clonedFromDeliverableId) {
        throw new Error('Cannot clone an already-cloned deliverable');
      }

      const user = auth.currentUser;

      // Create new deliverable data without projectId
      const clonedData = {
        title: `${originalDeliverable.title} (Clone)`,
        type: originalDeliverable.type || 'standard',
        description: originalDeliverable.description || '',
        aspectRatio: originalDeliverable.aspectRatio || '16:9',
        resolution: originalDeliverable.resolution || '1080p',
        runtime: originalDeliverable.runtime || '',
        assignedUserId: originalDeliverable.assignedUserId || null,
        // Keep companyId for access control
        companyId: originalDeliverable.companyId,
        // Detach from project
        projectId: null,
        // Reference to original
        clonedFromDeliverableId: deliverableId,
        // Reset status/stage for fresh start
        status: 'ready',
        stage: 'not_ready',
        // Clear due date, production events, and dependencies (project-scoped)
        dueDate: null,
        productionEventIds: [],
        dependencies: [],
        linkedDeliverableIds: [],
        isPrimaryEpisodeDeliverable: false,
        // Set creation metadata
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        createdByName: user?.displayName || user?.email || null
      };

      const deliverablesRef = collection(db, 'deliverables');
      const docRef = await addDoc(deliverablesRef, clonedData);

      // Log the cloning activity on the original
      await this.logActivity(
        deliverableId,
        'cloned',
        null,
        null,
        docRef.id,
        `Deliverable cloned and detached. New deliverable ID: ${docRef.id}`
      );

      // Log creation on the new one
      await this.logActivity(
        docRef.id,
        'created_from_clone',
        'clonedFromDeliverableId',
        null,
        deliverableId,
        `Created as detached clone from deliverable: ${originalDeliverable.title}`
      );

      this.logger.info('DeliverableService', `Deliverable ${deliverableId} cloned and detached. New ID: ${docRef.id}`);
      this.toast.success('Deliverable cloned and detached successfully');

      return { success: true, id: docRef.id, clonedDeliverable: { id: docRef.id, ...clonedData } };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to clone and detach deliverable: ${error.message}`);
      this.toast.error('Failed to clone deliverable');
      throw error;
    }
  }

  // Get original deliverable's project brief (for dynamic fetching on cloned deliverables)
  async getOriginalProjectBrief(deliverableId) {
    try {
      const deliverable = await this.getDeliverable(deliverableId);

      if (!deliverable) {
        return { success: false, brief: null, error: 'Deliverable not found' };
      }

      if (!deliverable.clonedFromDeliverableId) {
        return { success: false, brief: null, error: 'Not a cloned deliverable' };
      }

      // Get the original deliverable
      let originalDeliverable;
      try {
        originalDeliverable = await this.getDeliverable(deliverable.clonedFromDeliverableId);
      } catch {
        return { success: false, brief: null, error: 'Original deliverable deleted', originalDeleted: true };
      }

      if (!originalDeliverable) {
        return { success: false, brief: null, error: 'Original deliverable deleted', originalDeleted: true };
      }

      if (!originalDeliverable.projectId) {
        return { success: false, brief: null, error: 'Original has no project', originalProject: null };
      }

      // Fetch the project
      const projectRef = doc(db, 'projects', originalDeliverable.projectId);
      const projectSnap = await getDoc(projectRef);

      if (!projectSnap.exists()) {
        return { success: false, brief: null, error: 'Project deleted', projectDeleted: true };
      }

      const projectData = projectSnap.data();

      // Check for brief in multiple field names (breif is a known typo in the codebase)
      const brief = projectData.breif || projectData.brief || projectData.briefData || null;

      return {
        success: true,
        brief,
        projectId: originalDeliverable.projectId,
        projectName: projectData.name,
        originalDeliverableId: deliverable.clonedFromDeliverableId,
        originalDeliverableTitle: originalDeliverable.title
      };
    } catch (error) {
      this.logger.error('DeliverableService', `Failed to get original project brief: ${error.message}`);
      return { success: false, brief: null, error: error.message };
    }
  }
}

export const deliverableService = new DeliverableService();